/**
 * App shell: a 64px icon rail on desktop (UniFi-style — icons only, the
 * item name appears as a tooltip on hover), a slim top bar + slide-out
 * drawer on phones/tablets. Brand mark "Bruce" sits at the top of the
 * rail; sign-out is pinned to the bottom. Pages render in the main
 * area on the app's dark ground with responsive gutters.
 */
import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  SpaceDashboardOutlined as DashboardIcon,
  AccountBalanceWalletOutlined as PortfolioIcon,
  SwapVertOutlined as TradeIcon,
  PersonOutlined as ProfileIcon,
  SmartToyOutlined as RobotIcon,
  QueryStatsOutlined as ResearchIcon,
  ArticleOutlined as ReportsIcon,
  AccountTreeOutlined as SystemIcon,
  LogoutOutlined as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { C, RAIL_WIDTH } from '../../theme';

interface NavItem { text: string; icon: React.ReactElement; path: string }

const NAV: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Portfolio', icon: <PortfolioIcon />, path: '/portfolio' },
  { text: 'Trade', icon: <TradeIcon />, path: '/trade' },
  { text: 'Research', icon: <ResearchIcon />, path: '/research' },
  { text: 'Bots', icon: <RobotIcon />, path: '/robot' },
  { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
  { text: 'System', icon: <SystemIcon />, path: '/system' },
  { text: 'Profile', icon: <ProfileIcon />, path: '/profile' },
];

const isActive = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(path + '/')
  || (path === '/dashboard' && pathname.startsWith('/dashboard'))
  || (path === '/robot' && pathname.startsWith('/strategy'));

/** The "B" monogram — brand mark for the rail and the mobile bar. */
export const BruceMark: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <Box aria-label="Bruce" sx={{
    width: size, height: size, borderRadius: '10px', flex: '0 0 auto',
    display: 'grid', placeItems: 'center',
    background: `linear-gradient(135deg, ${C.accent} 0%, #6AA6EE 100%)`,
    color: '#07101F', fontWeight: 800, fontSize: size * 0.5, letterSpacing: '-.02em',
    boxShadow: `0 0 0 1px ${C.line2}, 0 6px 18px ${C.accent}33`,
  }}>B</Box>
);

const RailButton: React.FC<{ item: NavItem; active: boolean; onClick: () => void }> = ({ item, active, onClick }) => (
  <Tooltip title={item.text} placement="right" arrow enterDelay={150}>
    <IconButton
      onClick={onClick}
      aria-label={item.text}
      aria-current={active ? 'page' : undefined}
      sx={{
        width: 44, height: 44, borderRadius: '10px', position: 'relative',
        color: active ? C.ink : C.ink3,
        bgcolor: active ? 'rgba(57,135,229,.16)' : 'transparent',
        '&:hover': { color: C.ink, bgcolor: active ? 'rgba(57,135,229,.22)' : 'rgba(255,255,255,.06)' },
        '&::before': active ? {
          content: '""', position: 'absolute', left: -10, top: 11, bottom: 11, width: 3,
          borderRadius: 3, bgcolor: C.accent,
        } : undefined,
        '& svg': { fontSize: 22 },
      }}
    >
      {item.icon}
    </IconButton>
  </Tooltip>
);

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const go = (path: string) => { navigate(path); setMobileOpen(false); };
  const handleLogout = async () => { await logout(); navigate('/login'); };

  /* ---- desktop: icon rail ---- */
  const rail = (
    <Box component="nav" aria-label="Main" sx={{
      position: 'fixed', top: 0, left: 0, bottom: 0, width: RAIL_WIDTH, zIndex: theme.zIndex.drawer,
      display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center',
      py: '14px', gap: '6px', bgcolor: C.panel, borderRight: `1px solid ${C.line}`,
    }}>
      <Tooltip title="Bruce" placement="right" arrow>
        <Box component="button" onClick={() => go('/dashboard')} aria-label="Bruce — home"
          sx={{ all: 'unset', cursor: 'pointer', mb: '14px', display: 'grid' }}>
          <BruceMark />
        </Box>
      </Tooltip>
      {NAV.map(item => (
        <RailButton key={item.path} item={item} active={isActive(pathname, item.path)} onClick={() => go(item.path)} />
      ))}
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Sign out" placement="right" arrow enterDelay={150}>
        <IconButton onClick={handleLogout} aria-label="Sign out" sx={{
          width: 44, height: 44, borderRadius: '10px', color: C.ink3,
          '&:hover': { color: C.neg, bgcolor: 'rgba(230,103,103,.10)' },
        }}>
          <LogoutIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  /* ---- mobile: top bar + slide-out drawer (icons with labels) ---- */
  const drawer = (
    <Box role="presentation" sx={{ width: 248, pt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5 }}>
        <BruceMark size={30} />
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Bruce</Typography>
      </Box>
      <List sx={{ px: 1 }}>
        {NAV.map(item => {
          const active = isActive(pathname, item.path);
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton onClick={() => go(item.path)} selected={active} sx={{
                borderRadius: '10px', my: '2px',
                color: active ? C.ink : C.ink2,
                '&.Mui-selected': { bgcolor: 'rgba(57,135,229,.16)', '&:hover': { bgcolor: 'rgba(57,135,229,.22)' } },
              }}>
                <ListItemIcon sx={{ color: active ? C.accent : C.ink3, minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: active ? 600 : 500, fontSize: 14.5 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: '10px', color: C.ink2, '&:hover': { color: C.neg } }}>
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 14.5, fontWeight: 500 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bg, color: C.ink }}>
      {rail}

      {isMobile && (
        <>
          <AppBar position="fixed" sx={{ display: { md: 'none' } }}>
            <Toolbar variant="dense" sx={{ minHeight: 52, px: 1.5, gap: 1 }}>
              <IconButton edge="start" aria-label="Open menu" onClick={() => setMobileOpen(true)} sx={{ color: C.ink2 }}>
                <MenuIcon />
              </IconButton>
              <BruceMark size={28} />
              <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Bruce</Typography>
              <Typography sx={{ ml: 'auto', fontSize: 12.5, color: C.ink3 }}>
                {NAV.find(n => isActive(pathname, n.path))?.text ?? ''}
              </Typography>
            </Toolbar>
          </AppBar>
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}>
            {drawer}
          </Drawer>
        </>
      )}

      <Box component="main" sx={{
        ml: { xs: 0, md: `${RAIL_WIDTH}px` },
        pt: { xs: '52px', md: 0 },
        minWidth: 0,
      }}>
        <Box sx={{ p: { xs: '14px 12px 28px', sm: '18px 16px 32px', md: '24px 28px 40px' }, maxWidth: 1600, mx: 'auto', minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
